/* ===========================================================================
   PDP sticky bar — the chrome script.

   Three jobs, all of them things CSS cannot do on this theme:

     1. One frosted band behind the header and the bar — the same mechanism
        idol-a-z-theme/src/az-chrome.js runs for its letter rail, and the blog's
        breadcrumb trail before it. See "the glass" below.
     2. Dock the bar against the header's measured bottom edge, because
        Tapcart's install banner moves it and `--header-height` does not follow.
     3. Drop the mobile-only "hide near the footer" condition in theme.js.
   =========================================================================== */

(function () {
  "use strict";

  var root = document.documentElement;

  /* ————— 1 · The glass —————
     One fixed band runs from the top of the window to the bar's lower edge —
     the header's own recipe, 20% white under a 20px blur — so header and bar
     read as a single piece of glass. While it is on, Focal's own header glass
     and its `body::before` band step aside (body.nolae-glass-on,
     sticky-bar.css), or two blurs meet at a seam.

     The band is a real element at the end of <body>, in the ROOT stacking
     context, under the header (z 4) and the bar (z 9999). That placement is
     the whole trick: a pseudo-element on the header is the obvious way and it
     does not work — at `z-index: -1` inside the header's `position: sticky`
     stacking context the browser silently drops `backdrop-filter`, so it
     measures perfectly and renders nothing. */
  var band = null;
  var lastBand = -1;

  function ensureBand() {
    if (band) return true;
    if (!document.body) return false;
    band = document.createElement("div");
    band.className = "nolae-glass";
    band.setAttribute("aria-hidden", "true");
    document.body.appendChild(band);
    return true;
  }

  function glass() {
    var header = document.querySelector(".header");
    var group = header && header.parentElement;
    if (!band || !header || !group) return;

    var bar = document.querySelector("product-sticky-form");
    var height = 0;

    if (header.classList.contains("scrolled")) {
      // Down to the bar's lower edge once it is out, to the header's otherwise.
      var edge =
        bar && !bar.hasAttribute("hidden")
          ? bar.getBoundingClientRect().bottom
          : group.getBoundingClientRect().bottom;
      height = Math.max(0, Math.round(edge));
    }

    document.body.classList.toggle("nolae-glass-on", height > 0);
    if (height !== lastBand) {
      band.style.height = height + "px";
      lastBand = height;
    }
  }

  /* ————— 2 · Where the bar parks —————
     The theme docks it against --header-height, the header's OWN height. That
     is only right while the header group starts at the top of the window.
     Tapcart's install banner (aside#tapcart-web-banner) sits outside the header
     and pushes the whole group down, so the group's bottom ends up ~95px below
     --header-height and a bar docked against the variable lands on the search
     row. Measure the group's bottom edge instead.

     Nothing here assumes Tapcart: with no banner the measurement equals
     --header-height, which is why one value drives both breakpoints. The CSS
     falls back to the theme's own expression if this never runs. */
  var lastTop = -1;

  function publishTop() {
    var header = document.querySelector(".header");
    var group = header && header.parentElement;
    if (!group) return;
    var bottom = Math.max(0, group.getBoundingClientRect().bottom);
    if (bottom !== lastTop) {
      root.style.setProperty("--sticky-bar-top", bottom + "px");
      lastTop = bottom;
    }
  }

  /* ————— 3 · The footer rule —————
     theme.js decides visibility as

       matchMedia(breakpoints.pocket).matches
         ? hidden = !_isPaymentContainerPassed || _isFooterVisible
         : hidden = !_isPaymentContainerPassed

     so below 1000px — and only there — the bar also hides once the footer comes
     into view. This removes that one condition, so the bar stays docked to the
     end of the page on mobile the way it already does on desktop.

     A property patch, not a reimplementation: the theme's observer keeps
     running and keeps writing the flag, the setter swallows it and the getter
     always reads false. The reveal threshold, the slide and the desktop path
     stay exactly as the theme wrote them. The theme hid the bar there so it
     would not sit over the footer on a small screen — that is the trade. */
  function patchFooterRule() {
    var bar = document.querySelector("product-sticky-form");
    if (!bar || !bar.intersectionObserver) return false;
    if (bar.dataset.footerRuleRemoved === "true") return true;

    Object.defineProperty(bar, "_isFooterVisible", {
      get: function () {
        return false;
      },
      set: function () {},
      configurable: true,
    });
    bar.dataset.footerRuleRemoved = "true";
    bar.hidden = !bar._isPaymentContainerPassed;

    // The band follows the bar, so it has to hear about every toggle.
    new MutationObserver(glass).observe(bar, {
      attributes: true,
      attributeFilter: ["hidden"],
    });
    return true;
  }

  /* ————— Sold-out: offer the back-in-stock form instead of a dead button —————
     When the selected variant is sold out the theme swaps the bar's CTA to
     `button--ternary` + `disabled`. A disabled button is a dead end, so the bar
     offers Ordersify's "notify me" form instead — the same one the purple side
     tab opens.

     It opens that tab rather than reimplementing anything: Ordersify tracks the
     selected variant itself (ORDERSIFY_BIS.currentVariant), so clicking its own
     trigger gets the right variant for free. The label is read off that button
     too, which is what keeps this correct on all five storefront languages
     instead of hard-coding the German.

     If the app has no trigger on the page, nothing is swapped — the CSS just
     makes the disabled button read as disabled. */
  var syncingSoldOut = false;

  function syncSoldOut() {
    /* Re-entry guard. This function writes to the DOM, those writes are
       themselves mutations, and it is called BY a MutationObserver — without
       this it feeds itself and spins forever, locking the tab. Every write
       below is also made conditional, so a no-op sync produces no mutation at
       all and the observer stays quiet. */
    if (syncingSoldOut) return;
    syncingSoldOut = true;
    try {
      var bar = document.querySelector("product-sticky-form");
      if (!bar) return;
      var cta = bar.querySelector(
        ".product-form__add-button:not(.sticky-bar-notify)"
      );
      if (!cta) return;

      var soldOut = cta.hasAttribute("disabled");
      var trigger = document.querySelector(".osf_float_button");

      if (bar.classList.contains("is-sold-out") !== soldOut) {
        bar.classList.toggle("is-sold-out", soldOut);
      }

      var wantNotify = soldOut && !!trigger;
      if (!wantNotify) {
        if (bar.classList.contains("has-notify")) {
          bar.classList.remove("has-notify");
        }
        return;
      }

      var notify = bar.querySelector(".sticky-bar-notify");
      if (!notify) {
        notify = document.createElement("button");
        notify.type = "button";
        notify.className =
          "sticky-bar-notify product-form__add-button button button--primary";
        notify.addEventListener("click", function () {
          var t = document.querySelector(".osf_float_button");
          if (t) t.click();
        });
        cta.parentNode.insertBefore(notify, cta);
      }

      var label = trigger.textContent.trim();
      if (notify.textContent !== label) notify.textContent = label;

      /* Match the real CTA exactly, by copying its own computed metrics rather
         than restating them here. Two reasons it has to be done at runtime:
         an app rule forces the CTA's font-size and weight and does NOT match
         our button, so hard-coded values drift apart; and the theme gives bar
         buttons `line-height: 48px` at >= 1000px, which nothing overrides on
         our element — that alone made it 78px tall against the CTA's 41px.
         Copying keeps the two identical whatever the theme and apps do.
         `getComputedStyle` still resolves these while the CTA is display:none. */
      var from = getComputedStyle(cta);
      ["fontSize", "fontWeight", "lineHeight", "letterSpacing", "padding",
       "borderRadius", "textTransform"].forEach(function (prop) {
        if (notify.style[prop] !== from[prop]) notify.style[prop] = from[prop];
      });
      if (!bar.classList.contains("has-notify")) {
        bar.classList.add("has-notify");
      }
    } finally {
      // Released after the observer has drained the records this call made.
      setTimeout(function () { syncingSoldOut = false; }, 0);
    }
  }

  /* Has the sold-out state reached its final shape? Used by the boot retry
     loop, because Ordersify loads AFTER this script: at first run its trigger
     does not exist yet, so there is nothing to swap in, and the bar's own
     MutationObserver never fires again (the app appends its button to <body>,
     outside the subtree being watched). Without this the bar keeps a dead
     "Ausverkauft" button forever. */
  function soldOutResolved() {
    var bar = document.querySelector("product-sticky-form");
    if (!bar) return false;
    var cta = bar.querySelector(
      ".product-form__add-button:not(.sticky-bar-notify)"
    );
    if (!cta) return false;
    if (!cta.hasAttribute("disabled")) return true; // nothing to swap
    if (!document.querySelector(".osf_float_button")) return false; // not yet
    return !!bar.querySelector(".sticky-bar-notify"); // swapped
  }

  var soldOutQueued = false;

  function scheduleSoldOut() {
    if (soldOutQueued) return;
    soldOutQueued = true;
    requestAnimationFrame(function () {
      soldOutQueued = false;
      syncSoldOut();
    });
  }

  function watchSoldOut() {
    if (!document.body) return false;

    /* Observe <body>, not the bar. Changing a variant does not update the bar
       in place — the theme throws the whole subtree away and renders a new
       one, `product-rerender` element included (verified: the element identity
       changes). An observer attached to the bar is therefore watching a
       detached node from the first variant change onward, which is exactly why
       opening a sold-out variant directly worked while switching to one did
       not.

       Coalesced to one run per frame: <body> with `subtree` is a busy place on
       this theme, and syncSoldOut re-queries the document anyway, so bursts of
       mutations only need a single pass. Together with the re-entry guard and
       the conditional writes, a no-op sync costs a few querySelectors and
       mutates nothing. */
    new MutationObserver(scheduleSoldOut).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["disabled"],
    });
    syncSoldOut();
    return true;
  }

  function tick() {
    publishTop();
    glass();
  }

  function wire() {
    var header = document.querySelector(".header");
    if (!header) return false;
    addEventListener("scroll", tick, { passive: true });
    addEventListener("resize", tick, { passive: true });
    // Focal's header changes height as it settles after a scroll, with no
    // scroll event of its own; Tapcart's banner arrives later still.
    if (window.ResizeObserver) new ResizeObserver(tick).observe(header.parentElement);
    tick();
    return true;
  }

  var banded = ensureBand();
  var wired = banded && wire();
  var patched = patchFooterRule();
  var soldOutWatched = watchSoldOut();
  if (banded && wired && patched && soldOutWatched && soldOutResolved()) return;

  // theme.js is deferred, so the custom element is usually not upgraded yet.
  var tries = 0;
  var timer = setInterval(function () {
    if (!banded) banded = ensureBand();
    if (!wired) wired = banded && wire();
    if (!patched) patched = patchFooterRule();
    if (!soldOutWatched) soldOutWatched = watchSoldOut();
    // Cheap and idempotent, so just re-run it until Ordersify has arrived.
    else syncSoldOut();
    if (
      (banded && wired && patched && soldOutWatched && soldOutResolved()) ||
      ++tries > 100
    ) {
      clearInterval(timer);
    }
  }, 100);
})();
